import React, { useState } from 'react';

import { Accordion, AccordionSummary, AccordionDetails, Box, Button, Chip, IconButton, List, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { Add, Business, Delete, Edit, ExpandMore, LocationOnOutlined } from '@material-ui/icons';

import DepartmentListItem from './DepartmentListItem';

const useStyles = makeStyles((theme) => ({
  accordion: {
    '&.Mui-expanded': {
      margin: `${theme.spacing(2)}px 0`,
    },
    '&.MuiAccordion-root:before': {
      display: 'none',
    },
    borderRadius: 8,
    boxShadow: '0px 0px 10px 0px #0000001A',
    marginBottom: theme.spacing(2),
  },
  accordionActions: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
  },
  accordionDetails: {
    backgroundColor: 'white',
    padding: 20,
  },
  accordionHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  departmentCount: {
    backgroundColor: '#F3F3F3',
    color: '#010101',
    fontSize: '10px',
    fontWeight: 'bold',
  },
  departmentList: {
    '& .MuiListItemSecondaryAction-root': {
      display: 'none',
    },
    '&:hover': {
      '& .MuiListItemSecondaryAction-root': {
        display: 'flex',
      },
    },
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 0,
    width: '100%',
  },
  facilityInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 500,
  },
  facilityName: {
    fontSize: 14,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  root: {
    width: '100%',
  },
  subInfo: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: 12,
    gap: 16,
    marginLeft: 24,
  },
}));

const FacilityListItem = ({
  facility,
  expanded,
  onAccordionChange,
  onEditFacility,
  onDeleteFacility,
  onAddDepartment,
  onEditDepartment,
  onDeleteDepartment,
  onMouseEnter,
  onMouseLeave,
}) => {
  const classes = useStyles();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Accordion
      key={facility.id}
      className={classes.accordion}
      expanded={expanded === facility.id}
      onChange={onAccordionChange(facility.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <div className={classes.accordionHeader}>
          <Box display="flex" gridGap={8} alignItems="center" justifyContent="flex-start">
            <div className={classes.facilityInfo}>
              <Box display="flex" gridGap={8} alignItems="center" justifyContent="flex-start">
                <Business fontSize="small" />

                <Box className={classes.facilityName}>
                  <span style={{ fontWeight: 'bold' }}>{facility.name}</span>
                </Box>
              </Box>

              <Box alignItems="center" justifyContent="flex-start" flexDirection="row" className={classes.subInfo}>
                {facility.address && (
                  <div style={{ alignItems: 'center', display: 'flex', gap: 2 }}>
                    <LocationOnOutlined fontSize="inherit" />
                    {facility.address}
                  </div>
                )}

                {facility.id && <span className="ml-4"> ID: {facility.id}</span>}
                {facility.code && <span className="ml-2"> Code: {facility.code}</span>}
              </Box>
            </div>

            {facility.departments?.length > 0 && (
              <Chip
                size="small"
                label={`${facility.departments?.length} ${facility.departments?.length === 1 ? 'department' : 'departments'}`}
                className={classes.departmentCount}
              />
            )}
          </Box>

          <div className={classes.accordionActions}>
            {isHovered && (
              <>
                <Tooltip title="Edit Facility">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditFacility(facility.id);
                    }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={facility.departments?.length > 0 ? "You can't delete this facility because it has departments assigned to it." : 'Delete Facility'}>
                  <span>
                    <IconButton
                      size="small"
                      color="secondary"
                      disabled={facility.departments?.length > 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteFacility(facility.id);
                      }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}

            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAddDepartment(facility.id);
              }}>
              + Add Department
            </Button>
          </div>
        </div>
      </AccordionSummary>

      <AccordionDetails className={classes.accordionDetails}>
        {facility.departments?.length === 0 ? (
          <Box alignItems="center" justifyContent="center" gridGap={4} width="100%" gap={2} display="flex" flexDirection="column">
            <Typography variant="body2" color="textSecondary">
              No departments added yet.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ width: '200px' }}
              startIcon={<Add />}
              onClick={(e) => {
                e.stopPropagation();
                onAddDepartment(facility.id);
              }}>
              Add Department
            </Button>
          </Box>
        ) : (
          <List className={classes.departmentList}>
            {facility.departments?.map((department) => (
              <DepartmentListItem
                key={department.id}
                department={department}
                facilityId={facility.id}
                onEditDepartment={onEditDepartment}
                onDeleteDepartment={onDeleteDepartment}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            ))}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default FacilityListItem;
